/**
 * Shopify checkout utilities
 */

// The correct Shopify domain
const SHOPIFY_DOMAIN = 'sprezzatura-dev.myshopify.com';

/**
 * Debug logger for Shopify requests
 */
const debugLog = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`[SHOPIFY DEBUG ${timestamp}] ${message}`);
  if (data) {
    console.log(JSON.stringify(data, null, 2));
  }
};

/**
 * Intercepts and modifies fetch requests to shop.app for proper authentication
 */
export const setupShopifyCheckoutInterceptor = () => {
  debugLog(`Setting up Shopify checkout interceptor for domain: ${SHOPIFY_DOMAIN}`);
  
  // Add a console warning to help debug
  console.warn('[SHOPIFY INTERCEPTOR] Initializing checkout interceptor');
  
  // AGGRESSIVE SOLUTION: Block all shop.app requests at the network level
  // This uses both fetch override and XMLHttpRequest override
  
  // 1. Override fetch API
  const originalFetch = window.fetch;
  
  // 2. Override XMLHttpRequest
  const originalXHROpen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    // Check if this is a shop.app URL
    if (typeof url === 'string' && url.includes('shop.app')) {
      console.warn(`[XHR INTERCEPTOR] Blocking shop.app request: ${url}`);
      
      // Extract checkout ID if possible
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const checkoutId = pathParts[2]; // Extract checkout ID
        
        if (checkoutId && !isNaN(checkoutId)) {
          // Redirect to Shopify checkout directly
          const redirectUrl = `https://sprezzatura-dev.myshopify.com/checkout/${checkoutId}`;
          console.warn(`[XHR INTERCEPTOR] Redirecting to: ${redirectUrl}`);
          
          // Use setTimeout to avoid blocking the main thread
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 100);
        }
      } catch (e) {
        console.error('[XHR INTERCEPTOR] Error parsing URL:', e);
      }
      
      // Return without actually opening the connection
      // This effectively blocks the request
      return;
    }
    
    // For all other URLs, proceed normally
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };
  
  // 3. Override fetch function
  window.fetch = function(url, options = {}) {
    // Immediately log all requests for debugging
    console.log(`[FETCH INTERCEPTOR] Request to: ${typeof url === 'string' ? url : 'Request object'}`);
    
    // AGGRESSIVELY block any shop.app requests
    if (typeof url === 'string' && url.includes('shop.app')) {
      console.warn(`[FETCH INTERCEPTOR] BLOCKING direct shop.app request: ${url}`);
      
      // Extract checkout ID if possible
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        const checkoutId = pathParts[2]; // Extract checkout ID
        
        if (checkoutId && !isNaN(checkoutId)) {
          // Redirect to Shopify checkout directly
          const redirectUrl = `https://sprezzatura-dev.myshopify.com/checkout/${checkoutId}`;
          console.warn(`[FETCH INTERCEPTOR] Redirecting to: ${redirectUrl}`);
          
          // Use setTimeout to avoid blocking the main thread
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 100);
        }
      } catch (e) {
        console.error('[FETCH INTERCEPTOR] Error parsing URL:', e);
      }
      
      return Promise.reject(new Error('shop.app requests are blocked by the interceptor'));
    }
    
    // Log all outgoing requests for debugging
    debugLog(`Fetch request to: ${typeof url === 'string' ? url : 'Request object'}`, {
      method: options.method || 'GET',
      hasHeaders: !!options.headers,
      hasBody: !!options.body
    });
    
    // We're already handling shop.app URLs at the top of the function with our aggressive blocking
    // No need for additional shop.app handling here
    
    // Handle other private_access_tokens requests (non-shop.app)
    if (typeof url === 'string' && 
        (url.includes('private_access_tokens') || url.includes('sf_private_access_tokens'))) {
      debugLog(`Handling other private_access_tokens request: ${url}`);
      
      // Get the token from environment variables
      const token = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;
      let modifiedUrl = url;
      
      // Create new options with proper credentials and headers
      const newOptions = {
        ...options,
        credentials: 'include',
        mode: 'cors',
        headers: {
          ...(options.headers || {}),
          'X-Shopify-Storefront-Access-Token': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        }
      };
      
      // Make the request with proper authentication
      return originalFetch(modifiedUrl, newOptions)
        .then(response => {
          debugLog(`Response from ${modifiedUrl}: status ${response.status}`);
          return response;
        })
        .catch(error => {
          debugLog(`⚠️ ERROR with private_access_tokens request:`, error);
          throw error;
        });
    }
    
    // We're now handling all shop.app URLs at the top of the function
    // This section is kept for any non-shop.app checkout URLs that might need special handling
    if (typeof url === 'string' && url.includes('checkout') && !url.includes('shop.app')) {
      debugLog(`Handling non-shop.app checkout request: ${url}`);
      // Add any special handling for non-shop.app checkout URLs here if needed
    }
    
    // Handle Shopify API requests
    if (typeof url === 'string' && url.includes('shopify')) {
      debugLog(`Handling Shopify API request: ${url}`);
      
      // Get the token from environment variables
      const token = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;
      
      // Create new options with proper credentials and headers
      const newOptions = {
        ...options,
        credentials: 'include',
        mode: 'cors',
        headers: {
          ...(options.headers || {}),
          'X-Shopify-Storefront-Access-Token': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        }
      };
      
      debugLog('Modified Shopify API request options');
      
      // Make the request with modified options
      return originalFetch(url, newOptions)
        .then(response => {
          debugLog(`Response from ${url}: status ${response.status}`);
          if (!response.ok) {
            debugLog(`⚠️ ERROR: Shopify API request failed with status ${response.status}`);
          }
          return response;
        })
        .catch(error => {
          debugLog(`⚠️ FETCH ERROR for Shopify API:`, error);
          throw error;
        });
    }
    
    // For all other requests, use the original fetch
    return originalFetch(url, options);
  };
};

/**
 * Prepares the checkout by setting up necessary interceptors
 * Call this function early in your application initialization
 */
export const prepareShopifyCheckout = () => {
  setupShopifyCheckoutInterceptor();
};
